import { createContext, forwardRef, useCallback, useContext, useState } from "react";
import { useImperativeHandle } from "react";

const ManageIntegrationContext = createContext();

export const ManageIntegrationProvider = forwardRef(function ManageIntegrationProvider(
  { children, initialStep = 0, steps = [] },
  ref,
) {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [viewInstruction, setViewInstruction] = useState({ open: false, anchorEl: null });

  // Allow one past the last defined step (some integrations use a final success screen).
  const maxIndex = steps.length ? steps.length : 0;

  const toPreviousStep = useCallback(() => setActiveStep(s => Math.max(0, s - 1)), []);

  const toNextStep = useCallback(() => setActiveStep(s => Math.min(maxIndex, s + 1)), [maxIndex]);

  const goToStep = useCallback(
    idx => {
      const n = Number(idx) || 0;
      setActiveStep(Math.min(Math.max(0, n), maxIndex + 1));
    },
    [maxIndex],
  );

  const toggleInsruction = useCallback(event => {
    const el = event.currentTarget;
    setViewInstruction(prev => ({
      ...prev,
      open: !prev.open,
      anchorEl: prev.anchorEl ? null : el,
    }));
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      getActiveStep: () => activeStep,
      goToStep,
      toNextStep,
      toPreviousStep,
    }),
    [toNextStep, toPreviousStep, activeStep, goToStep],
  );

  return (
    <ManageIntegrationContext.Provider
      value={{
        activeStep,
        steps,
        viewInstruction,
        goToStep,
        toggleInsruction,
        toNextStep,
        toPreviousStep,
      }}
    >
      {children}
    </ManageIntegrationContext.Provider>
  );
});

export const useManageIntegrationContext = () => {
  const ctx = useContext(ManageIntegrationContext);
  if (!ctx) {
    throw new Error("useManageIntegrationContext must be used inside <ManageIntegrationProvider>");
  }
  return ctx;
};
